import { Component, inject, signal, computed, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { AuthSessionService } from './auth-session.service';
import { LogoComponent } from './logo.component';
import { I18nService, TranslatePipe } from './i18n.service';
import { aucklandClockParts, aucklandDateKey, aucklandHour, dateKeyEpoch } from './auckland-time';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [RouterLink, LogoComponent, FormsModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex flex-col pb-24 lg:pb-8 lg:min-h-[calc(100vh-72px)]" style="background:#F4F6F9;">
      <header class="sticky top-0 z-30 text-white shadow-sm lg:hidden" style="background:#173F73;">
        <div class="px-5 pt-4 pb-3 flex items-center justify-between">
          <app-logo theme="light"></app-logo>
          <div class="flex items-center gap-3">
            <button routerLink="/notifications" type="button" class="relative w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-white/90">
              <i class="ti ti-bell text-[19px]"></i>
              @if (unreadNotificationCount() > 0) {
                <span class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 border-2 border-[#173F73] text-[10px] font-bold leading-[14px] flex items-center justify-center">{{ unreadNotificationCount() > 9 ? '9+' : unreadNotificationCount() }}</span>
              }
            </button>
            <button routerLink="/profile" class="w-10 h-10 rounded-full bg-[#2B7BE4] border border-white/15 shrink-0 flex items-center justify-center overflow-hidden text-xs font-bold text-white">
              @if (profilePic()) {
                <img [src]="profilePic()" alt="Avatar" class="w-full h-full object-cover" referrerpolicy="no-referrer">
              } @else {
                {{ userInitials() }}
              }
            </button>
          </div>
        </div>
        <div class="h-px bg-white/15"></div>
        <div class="px-5 pt-3 pb-4 flex items-end justify-between gap-3">
          <div class="min-w-0">
            <p class="text-[12px] text-white/45 mb-1">{{ greeting() }}</p>
            <h1 class="text-[20px] leading-6 font-bold text-white truncate">{{ firstName() }}</h1>
          </div>
          <div class="flex flex-col items-end gap-1.5 shrink-0">
            <span class="text-[9px] text-white/40 flex items-center gap-1">
              {{ shiftHeaderStatus() }}
              <i class="ti ti-info-circle text-[11px]"></i>
            </span>
            <button
              (click)="openShiftConfirm()"
              [disabled]="shiftUpdating()"
              class="h-9 px-4 rounded-full bg-white/10 border border-white/10 text-white text-[12px] font-bold transition-all active:scale-[0.98] flex items-center gap-2 disabled:opacity-60">
              @if (shiftUpdating()) {
                <i class="ti ti-loader animate-spin"></i>
              } @else {
                <span class="w-1.5 h-1.5 rounded-full"
                      [class.bg-red-400]="shiftActive()"
                      [class.bg-red-500]="!shiftActive()"
                      [class.animate-pulse]="shiftActive()"></span>
              }
              {{ shiftButtonLabel() }}
            </button>
          </div>
        </div>
      </header>

      <main class="flex-1 px-4 pt-5 space-y-5 lg:px-7 lg:pt-6 lg:space-y-6">
        @if (dashboardErrors().length) {
          <section class="bg-rose-50 border border-rose-100 rounded-2xl p-4 flex items-start gap-3">
            <i class="ti ti-alert-circle text-rose-500 text-xl shrink-0"></i>
            <div class="flex-1">
              <p class="text-sm font-semibold text-rose-800">Some dashboard data could not refresh</p>
              @for (error of dashboardErrors(); track error) {
                <p class="text-xs text-rose-600 mt-0.5">{{ error }}</p>
              }
            </div>
            <button (click)="refresh()" class="text-xs font-semibold text-rose-700 bg-white/70 border border-rose-100 rounded-lg px-3 py-1.5">{{ 'retry' | t }}</button>
          </section>
        }

        @if (locationStatusMessage()) {
          <section class="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-start gap-3">
            <i class="ti ti-map-pin-off text-amber-600 text-xl shrink-0"></i>
            <p class="text-sm font-semibold text-amber-800">{{ locationStatusMessage() }}</p>
          </section>
        }

        @if (showShiftConfirm()) {
          <div class="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4" style="background:rgba(15,23,42,0.48);">
            <div class="bg-white rounded-[28px] border border-slate-100 p-5 w-full max-w-[380px] shadow-2xl">
              <div class="w-12 h-1.5 rounded-full bg-slate-200 mx-auto mb-5 sm:hidden"></div>
              <div class="rounded-3xl p-5 text-white text-center" style="background:#173F73;">
                <p class="text-[11px] font-semibold text-white/55 uppercase tracking-widest">{{ 'currentShift' | t }}</p>
                <p class="font-mono text-[42px] leading-none font-bold mt-3">{{ shiftElapsedDisplay() }}</p>
                <p class="text-xs text-white/55 mt-3">Started {{ shiftStartLabel() }}</p>
              </div>
              @if (shiftTimeReviewMessage()) {
                <div class="mt-4 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-900 flex items-start gap-2">
                  <i class="ti ti-clock-exclamation mt-0.5"></i>
                  <div>
                    <p class="text-sm font-bold">Check this shift time</p>
                    <p class="text-xs mt-0.5">{{ shiftTimeReviewMessage() }}</p>
                  </div>
                </div>
              }
              @if (blockingWork().length > 0) {
                <div class="mt-4 rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
                  <i class="ti ti-alert-triangle mt-0.5"></i>
                  <div class="min-w-0 flex-1">
                    <p class="font-bold">Your shift cannot end yet</p>
                    <p class="text-xs mt-0.5">Stop or pause each item below, then try ending your shift again.</p>
                    <div class="mt-2 space-y-2">
                      @for (item of blockingWork(); track item.type + '-' + item.id + '-' + item.session_id) {
                        <div class="block rounded-xl border border-amber-200 bg-white/75 px-3 py-2">
                          <span class="block text-[10px] uppercase tracking-widest font-bold text-amber-600">{{ item.type }} · {{ item.status }}</span>
                          <span class="block text-sm font-bold text-slate-800 truncate mt-0.5">{{ item.title }} ({{ item.id }})</span>
                          @if (item.address) { <span class="block text-xs text-slate-500 truncate mt-0.5">{{ item.address }}</span> }
                          @if (item.type === 'Project' && item.session_id) { <span class="block text-[10px] text-amber-700 mt-1">Session #{{ item.session_id }} · Open project to pause or stop</span> }
                          <div class="grid grid-cols-2 gap-2 mt-2">
                            <a [routerLink]="blockingWorkRoute(item)" (click)="closeShiftConfirm()"
                               class="h-9 rounded-lg border border-amber-200 bg-white text-amber-800 text-xs font-bold flex items-center justify-center gap-1.5">
                              <i class="ti ti-external-link"></i> Open {{ item.type }}
                            </a>
                            @if (item.type === 'Project' && item.session_id) {
                              <button type="button" (click)="stopBlockingProjectSession(item)" [disabled]="stoppingProjectSessionId() === item.session_id"
                                      class="h-9 rounded-lg bg-red-500 text-white text-xs font-bold disabled:opacity-60 flex items-center justify-center gap-1.5">
                                <i [class]="stoppingProjectSessionId() === item.session_id ? 'ti ti-loader-2 animate-spin' : 'ti ti-player-stop-filled'"></i>
                                {{ stoppingProjectSessionId() === item.session_id ? 'Stopping...' : 'Stop session' }}
                              </button>
                            } @else if (item.type === 'Job') {
                              <button type="button" (click)="openBlockingJobPause(item)"
                                      class="h-9 rounded-lg bg-orange-600 text-white text-xs font-bold flex items-center justify-center gap-1.5">
                                <i class="ti ti-player-pause-filled"></i> Pause job
                              </button>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }
              <div class="grid grid-cols-2 gap-3 mt-5">
                <button type="button" (click)="closeShiftConfirm()" [disabled]="shiftUpdating()"
                        class="h-12 rounded-2xl bg-slate-50 border border-slate-100 text-slate-600 font-bold text-sm disabled:opacity-60">
                  {{ 'close' | t }}
                </button>
                <button type="button" (click)="confirmShiftAction()" [disabled]="shiftUpdating() || blockingWork().length > 0"
                        class="h-12 rounded-2xl bg-red-500 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                  @if (shiftUpdating()) {
                    <i class="ti ti-loader animate-spin"></i>
                  } @else {
                    <i class="ti ti-player-stop-filled"></i>
                    {{ 'stopShift' | t }}
                  }
                </button>
              </div>
            </div>
          </div>
        }

        @if (showBlockingJobPause()) {
          <div class="fixed inset-0 z-[90] overflow-y-auto p-4 flex items-start justify-center" style="background:rgba(15,23,42,0.52);">
            <div class="bg-white rounded-2xl border border-slate-100 p-5 w-full max-w-[380px] my-auto">
              <div class="w-12 h-12 rounded-2xl mx-auto mb-4 bg-orange-50 text-orange-600 flex items-center justify-center">
                <i class="ti ti-player-pause text-2xl"></i>
              </div>
              <h2 class="text-lg font-semibold text-slate-900 text-center">Pause active job</h2>
              <p class="text-sm text-slate-500 text-center mt-1">{{ blockingJobToPause()?.title || blockingJobToPause()?.id }}</p>
              <p class="text-xs text-orange-700 text-center mt-2">A reason is required so the office knows why this job was paused. After pausing, you can end your shift.</p>
              <label class="block mt-4">
                <span class="text-xs font-semibold text-slate-600 mb-1 block">Reason</span>
                <select [(ngModel)]="blockingJobPauseCategory" class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 outline-none text-sm">
                  @for (category of jobPauseCategories; track category) {
                    <option [value]="category">{{ category }}</option>
                  }
                </select>
              </label>
              <label class="block mt-3">
                <span class="text-xs font-semibold text-slate-600 mb-1 block">Remarks</span>
                <textarea [(ngModel)]="blockingJobPauseRemarks" rows="4" placeholder="Example: work finished for today, returning tomorrow..."
                          class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none text-sm text-slate-700 resize-none"></textarea>
                <span class="text-[11px] text-slate-400 mt-1 block">Enter at least 5 characters.</span>
              </label>
              <div class="grid grid-cols-2 gap-3 mt-5">
                <button type="button" (click)="closeBlockingJobPause()" [disabled]="pausingBlockingJob()"
                        class="h-11 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm disabled:opacity-60">Cancel</button>
                <button type="button" (click)="confirmBlockingJobPause()" [disabled]="pausingBlockingJob() || blockingJobPauseRemarks.trim().length < 5"
                        class="h-11 rounded-xl bg-orange-600 text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
                  @if (pausingBlockingJob()) { <i class="ti ti-loader-2 animate-spin"></i> Pausing... } @else { Pause job }
                </button>
              </div>
            </div>
          </div>
        }

        @if (showOdometerModal()) {
          <div class="fixed inset-0 z-[80] flex items-center justify-center p-4" style="background:rgba(15,23,42,0.45);">
            <form (ngSubmit)="submitOdometer()" class="bg-white rounded-2xl border border-slate-100 p-5 w-full max-w-[380px]">
              <div class="w-12 h-12 rounded-2xl mx-auto mb-4 bg-sky-50 text-[#1A6FA6] flex items-center justify-center">
                <i class="ti ti-gauge text-2xl"></i>
              </div>
              <h2 class="text-lg font-semibold text-slate-900 text-center">Update Odometer</h2>
              <p class="text-sm text-slate-500 text-center mt-2">
                {{ myVehicle()?.rego }} current reading is {{ myVehicle()?.odo || 'not recorded' }}.
              </p>

              <div class="space-y-3 mt-4">
                <label class="block">
                  <span class="text-xs font-medium text-slate-500 mb-1 block">New Reading</span>
                  <input type="number" [(ngModel)]="odometerForm.odometer_km" name="odometer_km" min="0" required
                         class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                </label>
                <label class="block">
                  <span class="text-xs font-medium text-slate-500 mb-1 block">Notes</span>
                  <textarea [(ngModel)]="odometerForm.notes" name="notes" rows="3"
                            class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm resize-none"
                            placeholder="Optional"></textarea>
                </label>
              </div>

              <div class="grid grid-cols-2 gap-3 mt-5">
                <button type="button" (click)="closeOdometerModal()" [disabled]="odometerSaving()"
                        class="h-11 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm disabled:opacity-60">
                  Cancel
                </button>
                <button type="submit" [disabled]="odometerSaving()"
                        class="h-11 rounded-xl bg-[#102C41] text-white font-semibold text-sm disabled:opacity-60">
                  @if (odometerSaving()) {
                    <i class="ti ti-loader animate-spin"></i>
                  } @else {
                    Save
                  }
                </button>
              </div>
            </form>
          </div>
        }

        @if (activeJobs().length > 0) {
          <section class="rounded-2xl border border-emerald-100 bg-white overflow-hidden shadow-sm">
            <div class="w-full h-11 px-4 bg-white flex items-center justify-between">
              <span class="text-sm font-bold text-slate-800 flex items-center gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                In Progress
                <span class="text-xs text-slate-400">{{ activeJobs().length }}</span>
              </span>
              <span class="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Live Jobs</span>
            </div>
            <div class="px-3 pb-3 space-y-2 bg-emerald-50/40">
              @for (job of activeJobs(); track job.job_id) {
                <a [routerLink]="['/job', job.job_id]"
                   class="block rounded-xl p-4 text-white active:scale-[0.98] transition-all"
                   style="background:linear-gradient(135deg,#0F334A,#145B7A); border:1px solid rgba(255,255,255,0.12);">
                  <div class="flex items-start justify-between gap-3 mb-2">
                    <div class="min-w-0">
                      <div class="flex items-center gap-2 mb-1.5">
                        <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase bg-white/10 border border-white/15 text-white/80">{{ job.job_type }}</span>
                        <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase bg-emerald-400/15 border border-emerald-200/25 text-emerald-100">Live</span>
                      </div>
                      <h3 class="font-semibold text-base truncate">{{ job.customer }}</h3>
                      <p class="text-[11px] text-white/55 uppercase tracking-wider">{{ job.job_id }}</p>
                    </div>
                    <div class="text-right shrink-0">
                      <p class="text-[10px] text-white/55 uppercase tracking-wider">Elapsed</p>
                      <p class="font-mono text-lg font-semibold">{{ activeTimer(job) }}</p>
                    </div>
                  </div>
                  <p class="text-xs text-white/70 truncate">
                    <i class="ti ti-map-pin"></i> {{ job.address }}
                  </p>
                  @if (shiftActive()) {
                    <p class="mt-3 text-[10px] font-semibold text-emerald-100/80 uppercase tracking-wider">Pause or finish this job before ending your shift</p>
                  }
                </a>
              }
            </div>
          </section>
        }

        <section class="grid grid-cols-4 gap-2.5 lg:gap-4">
          <div class="h-[66px] lg:h-[86px] bg-white rounded-xl lg:rounded-lg px-2 lg:px-5 py-2.5 border border-slate-200/70 shadow-sm flex flex-col items-center lg:items-start justify-center">
            <p class="text-[22px] leading-6 font-bold text-slate-900">{{ activeJobs().length }}</p>
            <p class="text-[9px] font-bold text-slate-400 mt-1">{{ 'live' | t }}</p>
          </div>
          <div class="h-[66px] lg:h-[86px] bg-white rounded-xl lg:rounded-lg px-2 lg:px-5 py-2.5 border border-slate-200/70 shadow-sm flex flex-col items-center lg:items-start justify-center">
            <p class="text-[22px] leading-6 font-bold text-orange-500">{{ pausedJobs().length }}</p>
            <p class="text-[9px] font-bold text-slate-400 mt-1">{{ 'paused' | t }}</p>
          </div>
          <div class="h-[66px] lg:h-[86px] bg-white rounded-xl lg:rounded-lg px-2 lg:px-5 py-2.5 border border-slate-200/70 shadow-sm flex flex-col items-center lg:items-start justify-center">
            <p class="text-[22px] leading-6 font-bold text-slate-900">{{ doneJobsCount() }}</p>
            <p class="text-[9px] font-bold text-slate-400 mt-1">{{ 'done' | t }}</p>
          </div>
          <div class="h-[66px] lg:h-[86px] bg-white rounded-xl lg:rounded-lg px-2 lg:px-5 py-2.5 border border-slate-200/70 shadow-sm flex flex-col items-center lg:items-start justify-center">
            <p class="text-[22px] leading-6 font-bold text-[#7C5CFF]">{{ upcomingJobs().length }}</p>
            <p class="text-[9px] font-bold text-slate-400 mt-1">{{ 'upcoming' | t }}</p>
          </div>
        </section>

        @if (false) {
        <section class="bg-white rounded-2xl border border-slate-100 p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Shift Timing</p>
              <h2 class="text-base font-semibold text-slate-900">{{ shiftTimingTitle() }}</h2>
            </div>
            <span class="text-xs font-semibold px-2.5 py-1 rounded-full"
                  [class.bg-emerald-50]="shiftActive()"
                  [class.text-emerald-700]="shiftActive()"
                  [class.bg-slate-100]="!shiftActive()"
                  [class.text-slate-600]="!shiftActive()">
              {{ shiftActive() ? 'Running' : 'Today' }}
            </span>
          </div>

          <div class="grid grid-cols-3 gap-2.5 mt-4">
            <div class="rounded-2xl bg-slate-50 border border-slate-100 p-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Start</p>
              <p class="text-sm font-semibold text-slate-900">{{ shiftStartLabel() }}</p>
            </div>
            <div class="rounded-2xl bg-slate-50 border border-slate-100 p-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">End</p>
              <p class="text-sm font-semibold text-slate-900">{{ shiftEndLabel() }}</p>
            </div>
            <div class="rounded-2xl bg-slate-50 border border-slate-100 p-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Total</p>
              <p class="text-sm font-semibold text-slate-900">{{ shiftHours() }}h</p>
            </div>
          </div>

          @if (todayShiftSessions().length > 1) {
            <p class="text-xs text-slate-500 mt-3">{{ todayShiftSessions().length }} shift sessions recorded today.</p>
          }
        </section>

        <section class="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Today’s Work</p>
              <h2 class="text-base font-semibold text-slate-900">Next field actions</h2>
            </div>
            <span class="text-xs font-semibold text-slate-500">Shift {{ shiftHours() }}h</span>
          </div>

          @if (activeJobs().length === 0 && pausedJobs().length === 0 && nextWorkItems().length === 0) {
            <div class="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-5 text-center">
              <i class="ti ti-calendar-check text-2xl text-slate-300"></i>
              <p class="text-sm font-semibold text-slate-700 mt-2">No immediate job actions</p>
              <p class="text-xs text-slate-500 mt-0.5">New scheduled or approved work will appear here.</p>
            </div>
          } @else {
            @for (job of activeJobs().slice(0, 1); track job.job_id) {
              <a [routerLink]="['/job', job.job_id]" class="block rounded-2xl p-4 text-white active:scale-[0.98] transition-all"
                 style="background:linear-gradient(135deg,#0F334A,#145B7A); border:1px solid rgba(255,255,255,0.12);">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase bg-emerald-400/15 border border-emerald-200/25 text-emerald-100">Live now</span>
                    <h3 class="font-semibold text-base truncate mt-2">{{ job.customer }}</h3>
                    <p class="text-xs text-white/65 truncate mt-1"><i class="ti ti-map-pin"></i> {{ job.address }}</p>
                  </div>
                  <div class="text-right shrink-0">
                    <p class="text-[10px] text-white/55 uppercase tracking-wider">Elapsed</p>
                    <p class="font-mono text-lg font-semibold">{{ activeTimer(job) }}</p>
                  </div>
                </div>
              </a>
            }

            @for (job of pausedJobs().slice(0, 1); track job.job_id) {
              <a [routerLink]="['/job', job.job_id]" class="bg-orange-50 rounded-2xl border border-orange-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all">
                <div class="w-11 h-11 rounded-xl bg-white text-orange-600 border border-orange-100 flex items-center justify-center shrink-0">
                  <i class="ti ti-player-pause-filled text-xl"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-[9px] font-semibold uppercase text-orange-700">Paused - resume when ready</p>
                  <h3 class="font-semibold text-sm text-slate-900 truncate mt-0.5">{{ job.customer }}</h3>
                  <p class="text-xs text-orange-700 truncate mt-0.5">{{ pausedLabel(job) }}</p>
                </div>
                <i class="ti ti-chevron-right text-orange-300"></i>
              </a>
            }

            @for (job of nextWorkItems(); track job.job_id) {
              <a [routerLink]="['/job', job.job_id]" class="bg-slate-50 rounded-2xl border border-slate-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all">
                <div class="w-11 h-11 rounded-xl bg-white text-[#1A6FA6] border border-sky-100 flex flex-col items-center justify-center shrink-0">
                  <span class="text-[9px] font-semibold uppercase">{{ job.timeParts[1] }}</span>
                  <span class="text-sm font-semibold">{{ job.timeParts[0] }}</span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-[9px] font-semibold uppercase text-[#1A6FA6]">{{ nextActionLabel(job) }}</p>
                  <h3 class="font-semibold text-sm text-slate-900 truncate mt-0.5">{{ job.customer }}</h3>
                  <p class="text-xs text-slate-500 truncate mt-0.5"><i class="ti ti-map-pin"></i> {{ job.address }}</p>
                </div>
                <i class="ti ti-chevron-right text-slate-300"></i>
              </a>
            }
          }
        </section>

        }

        @if (myVehicle()) {
          <section class="bg-white rounded-2xl border border-slate-100 p-4">
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-start gap-3 min-w-0">
                <div class="w-11 h-11 rounded-xl bg-sky-50 text-[#1A6FA6] border border-sky-100 flex items-center justify-center shrink-0">
                  <i class="ti ti-truck text-xl"></i>
                </div>
                <div class="min-w-0">
                  <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Assigned Vehicle</p>
                  <h2 class="text-base font-semibold text-slate-900 truncate">{{ myVehicle()?.rego }} - {{ myVehicle()?.vehicle }}</h2>
                  <p class="text-xs text-slate-500 mt-0.5">Odometer {{ myVehicle()?.odo }} · Service {{ myVehicle()?.service }}</p>
                </div>
              </div>
              <button type="button" (click)="openOdometerModal()" class="h-9 px-3 rounded-xl bg-[#102C41] text-white text-xs font-semibold shrink-0">
                Update
              </button>
            </div>
            @if (vehicleAlerts().length) {
              <div class="mt-3 space-y-2">
                @for (alert of vehicleAlerts(); track alert.label) {
                  <div class="rounded-xl px-3 py-2 border text-xs"
                       [class.bg-rose-50]="alert.tone === 'danger'"
                       [class.text-rose-700]="alert.tone === 'danger'"
                       [class.border-rose-100]="alert.tone === 'danger'"
                       [class.bg-amber-50]="alert.tone === 'warning'"
                       [class.text-amber-700]="alert.tone === 'warning'"
                       [class.border-amber-100]="alert.tone === 'warning'">
                    <strong>{{ alert.label }}</strong>
                    <span class="block mt-0.5">{{ alert.detail }}</span>
                  </div>
                }
              </div>
            }
          </section>
        }

        @if (isLoading()) {
          <section class="space-y-3">
            @for (n of [1,2,3,4]; track n) {
              <div class="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 animate-pulse">
                <div class="w-10 h-10 rounded-xl bg-slate-100"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 bg-slate-100 rounded-full w-1/3"></div>
                  <div class="h-4 bg-slate-100 rounded-full w-2/3"></div>
                </div>
              </div>
            }
          </section>
        } @else {
          <section class="rounded-2xl border border-red-200 bg-red-50/40 overflow-hidden">
            <button type="button" (click)="toggleSection('urgent')" class="w-full h-11 px-4 bg-white/70 flex items-center justify-between">
              <span class="text-sm font-bold text-red-600 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>{{ urgentWorkItems().length }} {{ 'urgentPausedWork' | t }}</span>
              <i class="ti ti-chevron-right text-slate-300 transition-transform" [class.rotate-90]="sectionOpen('urgent')"></i>
            </button>
            @if (sectionOpen('urgent')) {
              <div class="px-3 pb-3 space-y-2">
                @if (!urgentWorkItems().length) {
                  <div class="bg-white rounded-xl px-3 py-3 text-xs font-semibold text-slate-400">{{ 'noUrgentWork' | t }}</div>
                }
                @for (item of urgentWorkItems(); track item.key) {
                  <a [routerLink]="item.route" class="bg-white rounded-xl px-3 py-3 flex items-center gap-3 active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                         [class.bg-red-100]="item.tone === 'urgent'"
                         [class.text-red-700]="item.tone === 'urgent'"
                         [class.bg-orange-100]="item.tone === 'paused'"
                         [class.text-orange-700]="item.tone === 'paused'"
                         [class.bg-sky-100]="item.tone !== 'urgent' && item.tone !== 'paused'"
                         [class.text-sky-700]="item.tone !== 'urgent' && item.tone !== 'paused'">
                      <i [class]="item.icon"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <h3 class="font-bold text-sm text-slate-900 truncate">{{ item.title }}</h3>
                        <span class="text-[8px] font-bold uppercase rounded-full px-2 py-0.5"
                              [class.bg-red-50]="item.tone === 'urgent'"
                              [class.text-red-600]="item.tone === 'urgent'"
                              [class.bg-orange-50]="item.tone === 'paused'"
                              [class.text-orange-600]="item.tone === 'paused'"
                              [class.bg-sky-50]="item.tone !== 'urgent' && item.tone !== 'paused'"
                              [class.text-sky-600]="item.tone !== 'urgent' && item.tone !== 'paused'">{{ item.badge }}</span>
                      </div>
                      <p class="text-xs text-slate-500 truncate">{{ item.subtitle }}</p>
                    </div>
                  </a>
                }
              </div>
            }
          </section>

          <section class="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <button type="button" (click)="toggleSection('today')" class="w-full h-11 px-4 bg-white flex items-center justify-between">
              <span class="text-sm font-bold text-slate-800 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>{{ 'todayWork' | t }} <span class="text-xs text-slate-400">{{ todayWorkItems().length }}</span></span>
              <i class="ti ti-chevron-right text-slate-300 transition-transform" [class.rotate-90]="sectionOpen('today')"></i>
            </button>
            @if (sectionOpen('today')) {
              <div class="px-3 pb-3 space-y-2 bg-slate-50/60">
                @if (!todayWorkItems().length) {
                  <div class="bg-white rounded-xl px-3 py-3 text-xs font-semibold text-slate-400">{{ 'noTodayWork' | t }}</div>
                }
                @for (item of todayWorkItems(); track item.key) {
                  <a [routerLink]="item.route" class="bg-white rounded-xl px-3 py-3 flex items-center gap-3 active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl bg-sky-50 text-[#1A6FA6] flex items-center justify-center shrink-0"><i [class]="item.icon"></i></div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <h3 class="font-bold text-sm text-slate-900 truncate">{{ item.title }}</h3>
                        <span class="text-[8px] font-bold uppercase rounded-full px-2 py-0.5 bg-slate-50 text-slate-500">{{ item.badge }}</span>
                      </div>
                      <p class="text-xs text-slate-500 truncate">{{ item.subtitle }}</p>
                    </div>
                  </a>
                }
              </div>
            }
          </section>

          <section class="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <button type="button" (click)="toggleSection('upcoming')" class="w-full h-11 px-4 bg-white flex items-center justify-between">
              <span class="text-sm font-bold text-slate-800 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-violet-500"></span>{{ 'upcomingWork' | t }} <span class="text-xs text-slate-400">{{ upcomingWorkItems().length }}</span></span>
              <i class="ti ti-chevron-right text-slate-300 transition-transform" [class.rotate-90]="sectionOpen('upcoming')"></i>
            </button>
            @if (sectionOpen('upcoming')) {
              <div class="px-3 pb-3 space-y-2 bg-slate-50/60">
                @if (!upcomingWorkItems().length) {
                  <div class="bg-white rounded-xl px-3 py-3 text-xs font-semibold text-slate-400">{{ 'noUpcomingWork' | t }}</div>
                }
                @for (item of upcomingWorkItems(); track item.key) {
                  <a [routerLink]="item.route" class="bg-white rounded-xl px-3 py-3 flex items-center gap-3 active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl bg-violet-50 text-[#7C5CFF] flex items-center justify-center shrink-0"><i [class]="item.icon"></i></div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <h3 class="font-bold text-sm text-slate-900 truncate">{{ item.title }}</h3>
                        <span class="text-[8px] font-bold uppercase rounded-full px-2 py-0.5 bg-violet-50 text-[#7C5CFF]">{{ item.badge }}</span>
                      </div>
                      <p class="text-xs text-slate-500 truncate">{{ item.subtitle }}</p>
                    </div>
                  </a>
                }
              </div>
            }
          </section>

          <section class="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <button type="button" (click)="toggleSection('recent')" class="w-full h-11 px-4 bg-white flex items-center justify-between">
              <span class="text-sm font-bold text-slate-800 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>{{ 'recentlyCompleted' | t }} <span class="text-xs text-slate-400">{{ recentWorkItems().length }}</span></span>
              <i class="ti ti-chevron-right text-slate-300 transition-transform" [class.rotate-90]="sectionOpen('recent')"></i>
            </button>
            @if (sectionOpen('recent')) {
              <div class="px-3 pb-3 space-y-2 bg-slate-50/60">
                @if (!recentWorkItems().length) {
                  <div class="bg-white rounded-xl px-3 py-3 text-xs font-semibold text-slate-400">{{ 'noRecentWork' | t }}</div>
                }
                @for (item of recentWorkItems(); track item.key) {
                  <a [routerLink]="item.route" class="bg-white rounded-xl px-3 py-3 flex items-center gap-3 active:scale-[0.98] transition-all opacity-90">
                    <div class="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><i [class]="item.icon"></i></div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <h3 class="font-bold text-sm text-slate-900 truncate">{{ item.title }}</h3>
                        <span class="text-[8px] font-bold uppercase rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-600">{{ item.badge }}</span>
                      </div>
                      <p class="text-xs text-slate-500 truncate">{{ item.subtitle }}</p>
                    </div>
                  </a>
                }
              </div>
            }
          </section>

          @if (quoteJobs().length) {
            <section>
              <div class="flex items-center justify-between mb-2 px-1">
                <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Quote Follow-up</p>
                <a routerLink="/work-list/jobs-quotes" class="text-[11px] font-semibold text-[#1A6FA6]">View all</a>
              </div>
              <div class="space-y-3">
                @for (job of quoteJobs(); track job.job_id) {
                  <a [routerLink]="['/job', job.job_id]" class="bg-white rounded-2xl border border-amber-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                      <i class="ti ti-file-dollar text-xl"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-0.5">
                        <span class="text-[10px] font-semibold text-slate-500">{{ job.job_id }}</span>
                        <span class="text-[9px] font-medium uppercase text-amber-700">{{ job.status }}</span>
                      </div>
                      <h3 class="font-semibold text-sm text-slate-800 truncate">{{ job.customer }}</h3>
                      <p class="text-xs text-slate-500 truncate mt-0.5">{{ job.job_type }} quote workflow</p>
                    </div>
                    <i class="ti ti-chevron-right text-slate-300"></i>
                  </a>
                }
              </div>
            </section>
          }

          @if (false && officeJobs().length) {
            <section>
              <div class="flex items-center justify-between mb-2 px-1">
                <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Ready for Office</p>
                <a routerLink="/work-list/jobs-office" class="text-[11px] font-semibold text-[#1A6FA6]">View all</a>
              </div>
              <div class="space-y-3">
                @for (job of officeJobs(); track job.job_id) {
                  <a [routerLink]="['/job', job.job_id]" class="bg-white rounded-2xl border border-sky-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                      <i class="ti ti-send-check text-xl"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h3 class="font-semibold text-sm text-slate-800 truncate">{{ job.customer }}</h3>
                      <p class="text-xs text-slate-500 truncate mt-0.5">{{ job.job_id }} - final quote sent to office</p>
                    </div>
                    <i class="ti ti-chevron-right text-slate-300"></i>
                  </a>
                }
              </div>
            </section>
          }

          @if (false && myProjects().length) {
            <section>
              <div class="flex items-center justify-between mb-2 px-1">
                <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">My Projects</p>
                <a routerLink="/work-list/projects-assigned" class="text-[11px] font-semibold text-[#1A6FA6]">View all</a>
              </div>
              <div class="space-y-3">
                @for (proj of myProjects(); track proj.id) {
                  <a [routerLink]="['/project', proj.id]" class="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl bg-sky-50 text-[#1A6FA6] flex items-center justify-center shrink-0">
                      <i class="ti ti-building text-xl"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h3 class="font-semibold text-sm text-slate-800 truncate">{{ proj.name }}</h3>
                      <p class="text-xs text-slate-500 truncate mt-0.5"><i class="ti ti-map-pin"></i> {{ proj.address || '-' }}</p>
                    </div>
                    <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase border"
                          [class.bg-emerald-50]="proj.status === 'Completed'"
                          [class.text-emerald-700]="proj.status === 'Completed'"
                          [class.border-emerald-100]="proj.status === 'Completed'"
                          [class.bg-amber-50]="proj.status !== 'Completed'"
                          [class.text-amber-700]="proj.status !== 'Completed'"
                          [class.border-amber-100]="proj.status !== 'Completed'">{{ proj.status }}</span>
                  </a>
                }
              </div>
            </section>
          }

          @if (false && completedJobs().length) {
            <section>
              <div class="flex items-center justify-between mb-2 px-1">
                <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Recently Completed</p>
                <a routerLink="/work-list/jobs-completed" class="text-[11px] font-semibold text-[#1A6FA6]">View all</a>
              </div>
              <div class="space-y-3">
                @for (job of completedJobs(); track job.job_id) {
                  <a [routerLink]="['/job', job.job_id]" class="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all opacity-90">
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                         [class.bg-emerald-50]="job.status === 'Completed'"
                         [class.text-emerald-600]="job.status === 'Completed'"
                         [class.bg-rose-50]="job.status === 'Terminated'"
                         [class.text-rose-600]="job.status === 'Terminated'">
                      <i [class]="job.status === 'Terminated' ? 'ti ti-circle-x-filled text-xl' : 'ti ti-circle-check-filled text-xl'"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h3 class="font-semibold text-sm text-slate-800 truncate">{{ job.customer }}</h3>
                      <p class="text-xs text-slate-500 truncate mt-0.5">{{ job.job_id }} - {{ job.status }}</p>
                    </div>
                  </a>
                }
              </div>
            </section>
          }
        }
      </main>
    </div>
  `
})
export class DashboardComponent implements OnInit, OnDestroy {
  api = inject(ApiService);
  i18n = inject(I18nService);
  authSession = inject(AuthSessionService);

  plumberName = signal('Plumber');
  profilePic = signal('');
  shiftActive = signal(false);
  shiftUpdating = signal(false);
  showShiftConfirm = signal(false);
  shiftStartedAt = signal<Date | null>(null);
  shiftEndedAt = signal<Date | null>(null);
  todayShiftSessions = signal<Array<{ id: number; startedAt: Date | null; endedAt: Date | null; totalSeconds: number; status: string }>>([]);
  shiftBaseSeconds = signal(0);
  blockingWork = signal<any[]>([]);
  shiftTimeReviewMessage = signal('');
  stoppingProjectSessionId = signal<number | null>(null);
  showBlockingJobPause = signal(false);
  blockingJobToPause = signal<any | null>(null);
  pausingBlockingJob = signal(false);
  readonly jobPauseCategories = ['Waiting for Parts', 'Customer Not Available', 'Unsafe Site', 'Need Approval', 'Other'];
  blockingJobPauseCategory = this.jobPauseCategories[0];
  blockingJobPauseRemarks = '';

  isLoading = signal(true);
  unreadNotificationCount = signal(0);
  jobsError = signal('');
  shiftError = signal('');
  projectsError = signal('');
  locationStatusMessage = signal('');
  allJobs = signal<any[]>([]);
  myProjects = signal<any[]>([]);
  myVehicle = signal<any | null>(null);
  vehicleAlerts = signal<Array<{ label: string; detail: string; tone: 'danger' | 'warning' }>>([]);
  showOdometerModal = signal(false);
  odometerSaving = signal(false);
  tick = signal(0);
  odometerForm = { odometer_km: '', notes: '' };

  private timerId: ReturnType<typeof setInterval> | undefined;
  private locationTimerId: ReturnType<typeof setInterval> | undefined;
  private notificationTimerId: ReturnType<typeof setInterval> | undefined;
  openSections = signal<Record<'urgent' | 'today' | 'upcoming' | 'recent', boolean>>({
    urgent: true,
    today: true,
    upcoming: true,
    recent: true
  });

  greeting = computed(() => {
    const hour = aucklandHour();
    if (hour < 12) return this.i18n.translate('goodMorning');
    if (hour < 17) return this.i18n.translate('goodAfternoon');
    return this.i18n.translate('goodEvening');
  });
  firstName = computed(() => this.plumberName().trim().split(/\s+/)[0] || 'Plumber');
  userInitials = computed(() => {
    const parts = this.plumberName().trim().split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : (parts[0]?.slice(0, 2) || 'P')).toUpperCase();
  });
  shiftHeaderStatus = computed(() => {
    if (this.shiftActive()) return `Started ${this.shiftStartLabel()}`;
    if (this.shiftEndedAt()) return `Ended ${this.shiftEndLabel()}`;
    return 'Not started';
  });
  shiftButtonLabel = computed(() => this.shiftActive() ? this.i18n.translate('stopShift') : this.i18n.translate('startShift'));

  sectionOpen(key: 'urgent' | 'today' | 'upcoming' | 'recent'): boolean {
    return this.openSections()[key] !== false;
  }

  toggleSection(key: 'urgent' | 'today' | 'upcoming' | 'recent') {
    this.openSections.update(state => ({ ...state, [key]: !this.sectionOpen(key) }));
  }

  activeJobs = computed(() => this.allJobs().filter(j => j.status === 'In Progress'));
  pausedJobs = computed(() => this.allJobs().filter(j => j.status === 'Paused'));
  todayJobs = computed(() => this.allJobs().filter(j => ['Scheduled', 'Unassigned', 'Approved', 'Quote Sent'].includes(j.status) && j.scheduledDay === this.todayIso()));
  upcomingJobs = computed(() => this.allJobs().filter(j => this.isUpcomingJob(j)));
  nextWorkItems = computed(() => {
    const today = this.todayJobs().slice().sort((a, b) => this.jobSortTime(a) - this.jobSortTime(b));
    const unscheduled = this.upcomingJobs().filter(j => !j.scheduledDay);
    const future = this.upcomingJobs().filter(j => !!j.scheduledDay && j.scheduledDay > this.todayIso()).sort((a, b) => this.jobSortTime(a) - this.jobSortTime(b));
    return [...today, ...unscheduled, ...future].slice(0, 2);
  });
  quoteJobs = computed(() => this.allJobs().filter(j => j.status === 'Quote Sent'));
  officeJobs = computed(() => this.allJobs().filter(j => j.status === 'Ready for Office'));
  completedJobs = computed(() => this.allJobs().filter(j => ['Completed', 'Terminated'].includes(j.status)).slice(0, 5));
  doneJobsCount = computed(() => this.allJobs().filter(j => ['Ready for Office', 'Completed', 'Terminated'].includes(j.status)).length);
  projectWorkItems = computed(() => this.myProjects().flatMap((project: any) => this.mapProjectWorkItems(project)));
  urgentWorkItems = computed(() => [
    ...this.activeJobs().map(job => this.workItemFromJob(job, 'LIVE', 'active')),
    ...this.pausedJobs().map(job => this.workItemFromJob(job, 'PAUSED', 'paused')),
    ...this.allJobs().filter(j => j.job_type === 'Emergency' && !['Ready for Office', 'Completed', 'Terminated'].includes(j.status)).map(job => this.workItemFromJob(job, 'URGENT', 'urgent')),
    ...this.projectWorkItems().filter(item => item.status === 'In Progress' || item.isOverdue)
  ].slice(0, 4));
  todayWorkItems = computed(() => [
    ...this.todayJobs().map(job => this.workItemFromJob(job, job.job_type === 'Emergency' ? 'URGENT' : 'JOB', job.job_type === 'Emergency' ? 'urgent' : 'normal')),
    ...this.projectWorkItems().filter(item => item.isToday && item.status !== 'Completed' && item.status !== 'Cancelled')
  ].sort((a, b) => a.sort - b.sort).slice(0, 5));
  upcomingWorkItems = computed(() => [
    ...this.upcomingJobs().map(job => this.workItemFromJob(job, 'JOB', 'normal')),
    ...this.projectWorkItems().filter(item => item.isUpcoming && item.status !== 'Completed' && item.status !== 'Cancelled')
  ].sort((a, b) => a.sort - b.sort).slice(0, 5));
  recentWorkItems = computed(() => [
    ...this.completedJobs().map(job => this.workItemFromJob(job, 'JOB', 'done')),
    ...this.projectWorkItems().filter(item => item.status === 'Completed')
  ].slice(0, 5));
  dashboardErrors = computed(() => this.authSession.expired()
    ? []
    : [this.jobsError(), this.shiftError(), this.projectsError()].filter(Boolean));

  shiftHours = computed(() => {
    this.tick();
    const seconds = this.shiftBaseSeconds() + this.currentShiftElapsed();
    return (seconds / 3600).toFixed(1);
  });
  shiftElapsedDisplay = computed(() => {
    this.tick();
    const seconds = this.shiftBaseSeconds() + this.currentShiftElapsed();
    return this.formatDuration(seconds);
  });
  latestShiftSession = computed(() => this.todayShiftSessions().slice().reverse().find(shift => shift.startedAt) || null);
  shiftTimingTitle = computed(() => {
    if (this.shiftActive()) return 'Shift in progress';
    if (this.shiftEndedAt()) return 'Shift ended today';
    return 'Not started today';
  });
  shiftStartLabel = computed(() => {
    const startedAt = this.shiftStartedAt() || this.latestShiftSession()?.startedAt || null;
    return this.formatShiftTime(startedAt);
  });
  shiftEndLabel = computed(() => {
    if (this.shiftActive()) return 'Running';
    return this.formatShiftTime(this.shiftEndedAt());
  });

  ngOnInit() {
    const user = this.api.currentUser();
    if (user) {
      this.plumberName.set(user.name);
      this.profilePic.set(user.profile_photo_url || '');
    }
    this.refresh();
    this.loadNotificationCount();
    this.timerId = setInterval(() => this.tick.update(v => v + 1), 1000);
    this.notificationTimerId = setInterval(() => this.loadNotificationCount(), 45000);
  }

  ngOnDestroy() {
    if (this.timerId) clearInterval(this.timerId);
    if (this.locationTimerId) clearInterval(this.locationTimerId);
    if (this.notificationTimerId) clearInterval(this.notificationTimerId);
  }

  refresh() {
    this.isLoading.set(true);
    this.jobsError.set('');
    this.shiftError.set('');
    this.projectsError.set('');
    this.loadShift();
    this.loadMyVehicle();
    this.loadProjects();
    this.loadJobs();
    this.loadNotificationCount();
  }

  loadNotificationCount() {
    this.api.getNotifications({ limit: 1, unread_only: true }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.unreadNotificationCount.set(Number(res.data?.unread_count || 0));
        }
      },
      error: () => {
        this.unreadNotificationCount.set(0);
      }
    });
  }

  loadMyVehicle() {
    this.api.getMyFleetVehicle().subscribe({
      next: (res: any) => {
        const vehicle = res.success ? (res.data?.vehicle || null) : null;
        this.myVehicle.set(vehicle);
        this.vehicleAlerts.set(vehicle ? this.buildVehicleAlerts(vehicle) : []);
      },
      error: () => {
        this.myVehicle.set(null);
        this.vehicleAlerts.set([]);
      }
    });
  }

  loadShift() {
    this.api.getShiftStatus().subscribe({
      next: (res: any) => {
        if (res.success && res.data) this.applyShift(res.data);
      },
      error: () => {
        this.shiftError.set('Shift status could not be loaded.');
      }
    });
  }

  loadJobs() {
    this.api.getJobs().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success && res.data?.jobs) {
          this.allJobs.set(res.data.jobs.map((j: any) => this.mapJob(j)));
        } else {
          this.jobsError.set(res.message || 'Jobs could not be loaded.');
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        this.jobsError.set(err?.error?.message || 'Jobs could not be loaded.');
      }
    });
  }

  loadProjects() {
    this.api.getProjects().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.projects) {
          this.myProjects.set(res.data.projects.filter((p: any) => p.status !== 'Completed'));
        }
      },
      error: () => {
        this.projectsError.set('Projects could not be loaded.');
      }
    });
  }

  openShiftConfirm() {
    if (this.shiftUpdating()) return;
    if (this.shiftActive()) {
      this.showShiftConfirm.set(true);
      return;
    }
    this.toggleShift('start');
  }

  closeShiftConfirm() {
    if (this.shiftUpdating()) return;
    this.showShiftConfirm.set(false);
  }

  openOdometerModal() {
    const vehicle = this.myVehicle();
    if (!vehicle) return;
    this.odometerForm = {
      odometer_km: String(vehicle.odometer_km || this.kmNumber(vehicle.odo) || ''),
      notes: ''
    };
    this.showOdometerModal.set(true);
  }

  closeOdometerModal() {
    if (this.odometerSaving()) return;
    this.showOdometerModal.set(false);
  }

  submitOdometer() {
    const vehicle = this.myVehicle();
    if (!vehicle || this.odometerSaving()) return;
    const next = this.kmNumber(this.odometerForm.odometer_km);
    const current = this.kmNumber(vehicle.odo);
    if (next === null) {
      showToast('error', 'Enter a valid odometer reading.');
      return;
    }
    if (current !== null && next < current) {
      showToast('error', 'New reading cannot be lower than current reading.');
      return;
    }

    this.odometerSaving.set(true);
    this.api.updateMyFleetOdometer({
      id: vehicle.id,
      odometer_km: next,
      notes: this.odometerForm.notes || ''
    }).subscribe({
      next: (res: any) => {
        this.odometerSaving.set(false);
        if (res.success) {
          showToast('success', 'Odometer updated.');
          this.showOdometerModal.set(false);
          this.loadMyVehicle();
        } else {
          showToast('error', res.message || 'Odometer could not be updated.');
        }
      },
      error: (err: any) => {
        this.odometerSaving.set(false);
        showToast('error', err?.error?.message || 'Odometer could not be updated.');
      }
    });
  }

  confirmShiftAction() {
    if (this.blockingWork().length > 0) {
      showToast('error', this.blockingWorkMessage());
      return;
    }
    this.toggleShift('end');
  }

  blockingWorkRoute(item: any): any[] {
    return [item?.type === 'Project' ? '/project' : '/job', String(item?.id || '')];
  }

  blockingWorkMessage(): string {
    const labels = this.blockingWork().map(item => `${item.type}: ${item.title || item.id} (${item.id})`);
    return labels.length
      ? `Pause or stop active work first: ${labels.join('; ')}.`
      : 'Pause or stop active work before ending your shift.';
  }

  openBlockingJobPause(item: any) {
    if (item?.type !== 'Job' || this.pausingBlockingJob()) return;
    this.showShiftConfirm.set(false);
    this.blockingJobToPause.set(item);
    this.blockingJobPauseCategory = this.jobPauseCategories[0];
    this.blockingJobPauseRemarks = '';
    this.showBlockingJobPause.set(true);
  }

  closeBlockingJobPause() {
    if (this.pausingBlockingJob()) return;
    this.showBlockingJobPause.set(false);
    this.blockingJobToPause.set(null);
    this.blockingJobPauseRemarks = '';
  }

  confirmBlockingJobPause() {
    const item = this.blockingJobToPause();
    const jobId = String(item?.id || '');
    const remarks = this.blockingJobPauseRemarks.trim();
    if (!jobId || remarks.length < 5 || this.pausingBlockingJob()) return;

    this.pausingBlockingJob.set(true);
    this.api.updateJobWorkSession({
      job_id: jobId,
      action: 'pause',
      pause_category: this.blockingJobPauseCategory,
      pause_remarks: remarks
    }).subscribe({
      next: (res: any) => {
        this.pausingBlockingJob.set(false);
        if (res?.success) {
          this.closeBlockingJobPause();
          this.loadShift();
          this.loadJobs();
          showToast('success', 'Job paused. You can now end your shift.');
        } else {
          showToast('error', res?.message || 'Unable to pause this job.');
        }
      },
      error: (err: any) => {
        this.pausingBlockingJob.set(false);
        showToast('error', err?.error?.message || 'Unable to pause this job.');
      }
    });
  }

  stopBlockingProjectSession(item: any) {
    const sessionId = Number(item?.session_id || 0);
    if (sessionId <= 0 || this.stoppingProjectSessionId() !== null) return;
    if (!confirm(`Stop the active project session for ${item.title || item.id}? Logged time will be saved and you can then end your shift.`)) return;

    this.stoppingProjectSessionId.set(sessionId);
    this.api.stopBlockingProjectSession(sessionId).subscribe({
      next: (res: any) => {
        this.stoppingProjectSessionId.set(null);
        if (res?.success) {
          this.applyShift(res.data || {});
          showToast('success', res.message || 'Project work session stopped.');
        } else {
          showToast('error', res?.message || 'Unable to stop project work.');
        }
      },
      error: (err: any) => {
        this.stoppingProjectSessionId.set(null);
        showToast('error', err?.error?.message || 'Unable to stop project work.');
      }
    });
  }

  private toggleShift(action: 'start' | 'end') {
    if (this.shiftUpdating()) return;
    this.shiftUpdating.set(true);
    this.currentLocationPayload().then(location => {
      this.api.updateShift(action, location || undefined).subscribe({
        next: (res: any) => {
          this.shiftUpdating.set(false);
          if (res.success && res.data) {
            this.applyShift(res.data);
            this.showShiftConfirm.set(false);
            this.syncLocationTracking();
            showToast('success', action === 'start' ? 'Shift started.' : 'Shift ended.');
          } else {
            showToast('error', res.message || 'Unable to update shift.');
          }
        },
        error: (err: any) => {
          this.shiftUpdating.set(false);
          const blockers = err?.error?.data?.blocking_work;
          if (Array.isArray(blockers)) {
            this.blockingWork.set(blockers);
            this.showShiftConfirm.set(true);
          }
          showToast('error', err?.error?.message || 'Unable to update shift.');
        }
      });
    });
  }

  activeTimer(job: any): string {
    this.tick();
    if (!job.startedAt) return '00:00:00';
    const seconds = Math.max(0, Math.floor((Date.now() - job.startedAt.getTime()) / 1000));
    return this.formatDuration(seconds);
  }

  private applyShift(data: any) {
    const active = !!data.active;
    this.blockingWork.set(Array.isArray(data.blocking_work) ? data.blocking_work : []);
    this.shiftTimeReviewMessage.set(data.requires_time_review ? String(data.time_review_message || '') : '');
    const shiftSessions = Array.isArray(data.today_shifts)
      ? data.today_shifts.map((shift: any) => ({
          id: Number(shift.id || 0),
          startedAt: this.parseDate(shift.started_at),
          endedAt: this.parseDate(shift.ended_at),
          totalSeconds: Number(shift.total_seconds || 0),
          status: shift.status || ''
        }))
      : [];
    const latestSession = shiftSessions.slice().reverse().find((shift: any) => shift.startedAt) || null;
    const latestEndedSession = shiftSessions.slice().reverse().find((shift: any) => shift.endedAt) || null;
    const startedAt = this.parseDate(data.started_at) || (active ? latestSession?.startedAt || null : null);
    const endedAt = this.parseDate(data.ended_at || data.last_ended_at) || latestEndedSession?.endedAt || null;
    let baseSeconds = Number(data.today_seconds || 0);
    if (active && startedAt && !Number.isNaN(startedAt.getTime())) {
      const elapsed = Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000));
      baseSeconds = Math.max(0, baseSeconds - elapsed);
    }
    this.shiftActive.set(active);
    this.shiftStartedAt.set(startedAt);
    this.shiftEndedAt.set(endedAt);
    this.todayShiftSessions.set(shiftSessions);
    this.shiftBaseSeconds.set(baseSeconds);
    this.syncLocationTracking();
  }

  private syncLocationTracking() {
    if (this.locationTimerId) {
      clearInterval(this.locationTimerId);
      this.locationTimerId = undefined;
    }
    if (!this.shiftActive()) return;
    this.pushCurrentLocation();
    this.locationTimerId = setInterval(() => this.pushCurrentLocation(), 5 * 60 * 1000);
  }

  private pushCurrentLocation() {
    this.currentLocationPayload().then(location => {
      if (!location || !this.shiftActive()) return;
      this.api.updateLocation(location).subscribe({ error: () => {} });
    });
  }

  private currentLocationPayload(): Promise<{ latitude: number; longitude: number; accuracy_m?: number | null } | null> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.locationStatusMessage.set('Location is not available on this device.');
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.locationStatusMessage.set('');
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy_m: position.coords.accuracy ?? null
          });
        },
        error => {
          if (error.code === 1) {
            this.locationStatusMessage.set('Location permission is off. Enable location while on shift so dispatch can see your current area.');
          } else if (error.code === 3) {
            this.locationStatusMessage.set('Location could not be read in time. Check GPS or location services.');
          } else {
            this.locationStatusMessage.set('Location could not be read. Check GPS or location services.');
          }
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    });
  }

  private currentShiftElapsed(): number {
    if (!this.shiftActive() || !this.shiftStartedAt()) return 0;
    return Math.max(0, Math.floor((Date.now() - this.shiftStartedAt()!.getTime()) / 1000));
  }

  private mapJob(j: any) {
    const scheduledDate = j.scheduled_date ? new Date(j.scheduled_date) : null;
    const startedAt = j.started_at ? new Date(j.started_at) : null;
    let timeParts = ['--', ''];
    let scheduledDay = '';

    if (scheduledDate && !Number.isNaN(scheduledDate.getTime())) {
      scheduledDay = aucklandDateKey(scheduledDate);
      timeParts = aucklandClockParts(scheduledDate);
    }

    return {
      job_id: j.id,
      customer: j.contacts?.find((c: any) => c.contact_type === 'Customer' || c.type === 'Customer')?.name || j.customer_name || 'Unknown',
      address: j.address || 'Unknown Address',
      status: j.status,
      job_type: j.type || j.job_type || 'Service',
      scheduledDay,
      timeParts,
      pausedAt: j.pause_started_at || j.paused_at || null,
      startedAt: startedAt && !Number.isNaN(startedAt.getTime()) ? startedAt : null,
      scheduledAt: scheduledDate && !Number.isNaN(scheduledDate.getTime()) ? scheduledDate : null,
    };
  }

  private workItemFromJob(job: any, badge: string, tone: 'normal' | 'urgent' | 'paused' | 'active' | 'done') {
    const sort = job.scheduledAt instanceof Date ? job.scheduledAt.getTime() : Number.MAX_SAFE_INTEGER;
    const displayBadge = badge === 'JOB' && !job.scheduledDay ? 'SCHEDULE' : badge;
    return {
      key: `job-${job.job_id}-${displayBadge}`,
      source: 'Job',
      title: job.customer || job.address || job.job_id,
      subtitle: `${job.address || 'No address'} - ${job.job_type || 'Job'}`,
      status: job.status,
      badge: displayBadge,
      tone,
      icon: job.status === 'Paused' ? 'ti ti-tool' : (job.job_type === 'Emergency' ? 'ti ti-alert-triangle' : 'ti ti-briefcase'),
      route: ['/job', job.job_id],
      sort
    };
  }

  private mapProjectWorkItems(project: any) {
    const assignments = Array.isArray(project?.work_assignments) ? project.work_assignments : [];
    return assignments.map((assignment: any) => {
      const start = this.parseDateOnly(assignment.start_date);
      const end = this.parseDateOnly(assignment.end_date || assignment.start_date);
      const today = this.parseDateOnly(this.todayIso());
      const isToday = !!start && !!end && !!today && start.getTime() <= today.getTime() && end.getTime() >= today.getTime();
      const isUpcoming = !!start && !!today && start.getTime() > today.getTime();
      const isOverdue = !!end && !!today && end.getTime() < today.getTime() && !['Completed', 'Cancelled'].includes(assignment.status);
      return {
        key: `project-${project.id}-${assignment.id}`,
        source: 'Project',
        title: project.address || project.name || project.id,
        subtitle: assignment.instruction || project.name || 'Project work',
        status: assignment.status || 'Scheduled',
        badge: 'PROJECT',
        tone: isOverdue ? 'urgent' : (assignment.status === 'In Progress' ? 'active' : assignment.status === 'Completed' ? 'done' : 'normal'),
        icon: 'ti ti-building',
        route: ['/project', project.id],
        sort: start ? start.getTime() : Number.MAX_SAFE_INTEGER,
        isToday,
        isUpcoming,
        isOverdue
      };
    });
  }

  nextActionLabel(job: any): string {
    if (job.status === 'Approved') return 'Approved work';
    if (job.status === 'Quote Sent') return 'Quote follow-up';
    if (!job.scheduledDay) return 'Unscheduled';
    if (job.scheduledDay === this.todayIso()) return 'Scheduled today';
    return 'Upcoming';
  }

  pausedLabel(job: any): string {
    if (!job?.pausedAt) return 'Work is temporarily paused';
    const date = new Date(job.pausedAt);
    if (Number.isNaN(date.getTime())) return 'Work is temporarily paused';
    return `Paused since ${date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', timeZone: 'Pacific/Auckland' })}`;
  }

  private formatDuration(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  private formatShiftTime(value: Date | null | undefined): string {
    if (!value || Number.isNaN(value.getTime())) return '--';
    return value.toLocaleTimeString('en-NZ', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Pacific/Auckland'
    });
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private todayIso(): string {
    return aucklandDateKey();
  }

  private isUpcomingJob(job: any): boolean {
    if (!['Unassigned', 'Scheduled', 'Approved', 'Quote Sent'].includes(job.status)) return false;
    return !job.scheduledDay || job.scheduledDay >= this.todayIso();
  }

  private jobSortTime(job: any): number {
    return job.scheduledAt instanceof Date ? job.scheduledAt.getTime() : Number.MAX_SAFE_INTEGER;
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const [year, month, day] = String(value).split('-').map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private buildVehicleAlerts(vehicle: any): Array<{ label: string; detail: string; tone: 'danger' | 'warning' }> {
    const alerts: Array<{ label: string; detail: string; tone: 'danger' | 'warning' }> = [];
    const complianceDays = this.daysUntil(vehicle.wof);
    const complianceType = vehicle.complianceType || 'WOF';
    if (complianceDays !== null && complianceDays < 0) {
      alerts.push({ label: `${complianceType} overdue`, detail: `${Math.abs(complianceDays)} day${Math.abs(complianceDays) === 1 ? '' : 's'} overdue.`, tone: 'danger' });
    } else if (complianceDays !== null && complianceDays <= 30) {
      alerts.push({ label: `${complianceType} due soon`, detail: `${complianceDays} day${complianceDays === 1 ? '' : 's'} remaining.`, tone: 'warning' });
    }

    const serviceRemaining = this.kmRemaining(vehicle.odo, vehicle.service);
    if (serviceRemaining !== null && serviceRemaining < 0) {
      alerts.push({ label: 'Service overdue', detail: `${Math.abs(serviceRemaining).toLocaleString()} km past service due.`, tone: 'danger' });
    } else if (serviceRemaining !== null && serviceRemaining <= 2000) {
      alerts.push({ label: 'Service due soon', detail: `${serviceRemaining.toLocaleString()} km until service.`, tone: 'warning' });
    }

    const rucRemaining = this.kmRemaining(vehicle.odo, vehicle.ruc);
    if (rucRemaining !== null && rucRemaining < 0) {
      alerts.push({ label: 'RUC overdue', detail: `${Math.abs(rucRemaining).toLocaleString()} km past paid RUC.`, tone: 'danger' });
    } else if (rucRemaining !== null && rucRemaining <= 1000) {
      alerts.push({ label: 'RUC due soon', detail: `${rucRemaining.toLocaleString()} km remaining.`, tone: 'warning' });
    }
    return alerts;
  }

  private daysUntil(value: string): number | null {
    if (!value) return null;
    const [year, month, day] = String(value).split('-').map(Number);
    const targetKey = year && month && day ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` : aucklandDateKey(value);
    const target = dateKeyEpoch(targetKey);
    const today = dateKeyEpoch(aucklandDateKey());
    return target === null || today === null ? null : Math.ceil((target - today) / 86400000);
  }

  private kmRemaining(currentValue: string, targetValue: string): number | null {
    const current = this.kmNumber(currentValue);
    const target = this.kmNumber(targetValue);
    if (current === null || target === null) return null;
    return target - current;
  }

  private kmNumber(value: string | number): number | null {
    const numeric = String(value || '').replace(/[^0-9]/g, '');
    if (!numeric) return null;
    return Number(numeric);
  }
}
